/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {ChangeEvent, useCallback, useState} from 'react';
import debounce from 'lodash.debounce';
import {SavedMapViewType} from '../../../types/types';

export type UseSavedMapViewNameProps = {
  savedMapViews?: SavedMapViewType[];
  mapViewToEdit?: SavedMapViewType;
  onSave?: Function;
};

/**
 * Provides the saved map view name form state shared by both themes' save
 * forms: the entered name, whether saving is disabled because another saved
 * view already uses that name, and the (debounced) change and submit
 * handlers.
 *
 * ```typescript
 *
 *   import {useSavedMapViewName} from
 *     '@ion/dls/src/maps/components/views/useSavedMapViewName';
 *   ...
 *
 *   const {inputValue, saveDisabled, onNameChange, onSubmit} = useSavedMapViewName({
 *     savedMapViews,
 *     mapViewToEdit,
 *     onSave
 *   });
 *
 * ```
 *
 * @param   props The saved views, the view being edited and the save handler.
 * @returns       The name form state and handlers.
 */
export const useSavedMapViewName = ({savedMapViews, mapViewToEdit, onSave}: UseSavedMapViewNameProps) => {
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [inputValue, setInputValue]     = useState(mapViewToEdit?.id || '');

  const onNameChange = useCallback(debounce(({target: {value}}: ChangeEvent<HTMLInputElement>) => {
    setInputValue(value);
    const match = !!(savedMapViews?.find(({id}) => value === id));
    setSaveDisabled(match && value !== mapViewToEdit?.id);
  }, 200), [savedMapViews, mapViewToEdit]);

  const onSubmit = useCallback(() => {
    onSave?.({inputValue});
  }, [inputValue, onSave]);

  return {inputValue, saveDisabled, onNameChange, onSubmit};
};

export default useSavedMapViewName;
